// @cursor:state-management
// @cursor:platforms
import React, { createContext, useContext, useState, useEffect } from 'react';
import { platforms } from '../config/platforms';
import { useAuth } from '../lib/useAuth';
import { getAuthHeaders } from '../lib/authFetch';

type PlatformKey = keyof typeof platforms;
type PostingMethod = 'intent' | 'oauth';

interface PlatformContextType {
  enabledPlatforms: Record<PlatformKey, boolean>;
  togglePlatform: (platform: PlatformKey) => void;
  platformConnections: Record<PlatformKey, boolean>;
  setPlatformConnections: React.Dispatch<React.SetStateAction<Record<PlatformKey, boolean>>>;
  postingMethods: Record<PlatformKey, PostingMethod>;
  setPostingMethod: (platform: PlatformKey, method: PostingMethod) => void;
  isBetaUser: boolean;
  hasLoadedPlatformMetadata: boolean;
}

const PlatformContext = createContext<PlatformContextType | undefined>(undefined);

const makeDefaultPlatformConnections = (): Record<PlatformKey, boolean> =>
  (Object.keys(platforms) as PlatformKey[]).reduce((acc, key) => {
    acc[key] = false;
    return acc;
  }, {} as Record<PlatformKey, boolean>);

export const PlatformProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [enabledPlatforms, setEnabledPlatforms] = useState<Record<PlatformKey, boolean>>(() => {
    const saved = localStorage.getItem('enabled_platforms');
    return saved ? JSON.parse(saved) : Object.fromEntries(
      Object.keys(platforms).map(key => [key, true]) // Enable all platforms by default
    );
  });

  const [postingMethods, setPostingMethods] = useState<Record<PlatformKey, PostingMethod>>(() => {
    const saved = localStorage.getItem('posting_methods');
    return saved ? JSON.parse(saved) : Object.fromEntries(
      Object.keys(platforms).map(key => [key, 'intent']) // Default to intent
    );
  });

  const [platformConnections, setPlatformConnections] = useState<Record<PlatformKey, boolean>>(makeDefaultPlatformConnections);
  const [isBetaUser, setIsBetaUser] = useState(false);
  const [hasLoadedPlatformMetadata, setHasLoadedPlatformMetadata] = useState(false);

  const { session } = useAuth();

  const refreshPlatformConnections = async () => {
    // TODO: get userId from auth context or similar
    const userId = session?.user?.id;
    if (!userId) return;
    try {
      const base = process.env.REACT_APP_API_URL;
      const isLocalHost = /^(localhost|127\.0\.0\.1|192\.168\.\d+\.\d+)$/.test(window.location.hostname);
      const url = isLocalHost
        ? `/api/user/platforms`
        : (base ? `${base.replace(/\/$/, '')}/api/user/platforms` : `/api/user/platforms`);
      console.debug('[PlatformContext] fetch platforms', { url, userId });
      const response = await fetch(url, { headers: await getAuthHeaders() });
      const contentType = response.headers.get('content-type') || '';
      const data = contentType.includes('application/json') ? await response.json() : { error: await response.text() };
      if (!response.ok) {
        console.error('[PlatformContext] fetch platforms error', { status: response.status, body: data });
        return;
      }
      if (data.platforms) {
        console.debug('[PlatformContext] platformConnections <-', data.platforms);
        setPlatformConnections((prev) => ({ ...prev, ...data.platforms }));
      }
      if (typeof data.isBetaUser === 'boolean') {
        setIsBetaUser(data.isBetaUser);
      }
    } catch (e) {
      // fail silently for now
    } finally {
      setHasLoadedPlatformMetadata(true);
    }
  };

  useEffect(() => {
    if (!session?.user?.id) {
      setPlatformConnections(makeDefaultPlatformConnections());
      setIsBetaUser(false);
      setHasLoadedPlatformMetadata(false);
      return;
    }

    setHasLoadedPlatformMetadata(false);
    refreshPlatformConnections();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session]);

  useEffect(() => {
    localStorage.setItem('enabled_platforms', JSON.stringify(enabledPlatforms));
  }, [enabledPlatforms]);

  useEffect(() => {
    localStorage.setItem('posting_methods', JSON.stringify(postingMethods));
  }, [postingMethods]);

  const togglePlatform = (platform: PlatformKey) => {
    setEnabledPlatforms(prev => ({
      ...prev,
      [platform]: !prev[platform]
    }));
  };

  const setPostingMethod = (platform: PlatformKey, method: PostingMethod) => {
    setPostingMethods(prev => ({
      ...prev,
      [platform]: method
    }));
  };

  return (
    <PlatformContext.Provider value={{ enabledPlatforms, togglePlatform, platformConnections, setPlatformConnections, postingMethods, setPostingMethod, isBetaUser, hasLoadedPlatformMetadata }}>
      {children}
    </PlatformContext.Provider>
  );
};

export const usePlatforms = () => {
  const context = useContext(PlatformContext);
  if (context === undefined) {
    throw new Error('usePlatforms must be used within a PlatformProvider');
  }
  return context;
}; 
